import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Settings() {
  const [form, setForm] = useState(null);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/categories")])
      .then(([s, c]) => {
        setForm({ ...s.data, default_category_id: s.data.default_category_id || "none" });
        setCategories(c.data);
      })
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  if (!form) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="settings-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-black/10 border-t-black" />
      </div>
    );
  }

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      payload.default_category_id = form.default_category_id === "none" ? null : form.default_category_id;
      const { data } = await api.put("/settings", payload);
      setForm({ ...data, default_category_id: data.default_category_id || "none" });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (pw.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pw.new_password !== pw.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-8" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-zinc-500">Your public page identity and preferences.</p>
      </div>

      <div className="space-y-5 rounded-3xl border border-black/5 bg-white p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-black/5 bg-zinc-50">
            {form.profile_image && !imgError ? (
              <img src={form.profile_image} alt="Profile" onError={() => setImgError(true)} className="h-full w-full object-cover" data-testid="settings-profile-preview" />
            ) : (
              <ImageOff className="h-5 w-5 text-zinc-300" />
            )}
          </div>
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="s-profile">Profile image URL</Label>
            <Input
              id="s-profile"
              value={form.profile_image}
              onChange={(e) => {
                setImgError(false);
                set("profile_image")(e);
              }}
              data-testid="settings-profile-image-input"
              className="rounded-xl"
              placeholder="https://..."
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="s-title">Page title</Label>
            <Input id="s-title" value={form.page_title} onChange={set("page_title")} data-testid="settings-page-title-input" className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-instagram">Instagram URL</Label>
            <Input id="s-instagram" value={form.instagram_url} onChange={set("instagram_url")} data-testid="settings-instagram-input" className="rounded-xl" placeholder="https://instagram.com/..." />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="s-bio">Bio / description</Label>
          <Textarea id="s-bio" value={form.bio} onChange={set("bio")} data-testid="settings-bio-input" className="rounded-xl" rows={2} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="s-youtube">YouTube URL</Label>
            <Input id="s-youtube" value={form.youtube_url} onChange={set("youtube_url")} data-testid="settings-youtube-input" className="rounded-xl" placeholder="Optional" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-twitter">Twitter / X URL</Label>
            <Input id="s-twitter" value={form.twitter_url} onChange={set("twitter_url")} data-testid="settings-twitter-input" className="rounded-xl" placeholder="Optional" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Default category for new products</Label>
            <Select value={form.default_category_id} onValueChange={set("default_category_id")}>
              <SelectTrigger data-testid="settings-default-category-select" className="rounded-xl">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-black/10 px-4 py-2.5 sm:mt-6">
            <div>
              <Label htmlFor="s-analytics" className="text-sm">
                Analytics tracking
              </Label>
              <p className="text-xs text-zinc-400">Record visits and clicks</p>
            </div>
            <Switch id="s-analytics" checked={form.analytics_enabled} onCheckedChange={set("analytics_enabled")} data-testid="settings-analytics-switch" />
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} data-testid="settings-save-button" className="w-full rounded-full bg-black py-5 text-white hover:bg-zinc-800">
          {saving ? "Saving..." : "Save settings"}
        </Button>
      </div>

      <div className="space-y-4 rounded-3xl border border-black/5 bg-white p-6">
        <div>
          <h2 className="font-heading text-lg font-medium tracking-tight">Change password</h2>
          <p className="text-xs text-zinc-400">Takes effect immediately for future logins.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pw-current">Current password</Label>
          <Input id="pw-current" type="password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} data-testid="password-current-input" className="rounded-xl" autoComplete="current-password" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="pw-new">New password</Label>
            <Input id="pw-new" type="password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} data-testid="password-new-input" className="rounded-xl" autoComplete="new-password" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pw-confirm">Confirm new password</Label>
            <Input id="pw-confirm" type="password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} data-testid="password-confirm-input" className="rounded-xl" autoComplete="new-password" />
          </div>
        </div>
        <Button onClick={handlePasswordChange} disabled={pwSaving || !pw.current_password || !pw.new_password} data-testid="password-change-button" variant="outline" className="rounded-full px-6">
          {pwSaving ? "Updating..." : "Update password"}
        </Button>
      </div>
    </div>
  );
}
